import { Box, Button, ButtonGroup, Heading, IconButton } from '@zillow/constellation-10';
import { IconChevronDownFilled, IconChevronUpFilled } from '@zillow/constellation-icons';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { TourSchedulingUpsell, useTourUpsellCookie } from '@zg-rentals/touring';
import { useZCalendarWebApiHandler_getTouringTreatmentForZuidAndListing } from '@zg-rentals/ts-z-calendar-client-react-query';
import useQueryParamsNext from '../../../hooks/useQueryParamsNext';
import type { DrawerProps } from '../../Drawer';
import Drawer from '../../Drawer';
import {
  emitLeadMgmtTourUpsellBannerClickEvent,
  LEAD_MNGMT_TOUR_UPSELL_BANNER_CLICK_EVENT_TYPE_ID,
} from '../../../constants/clickstreamEvents/tourScheduling';
import { OPEN_LEAD_PROFILE_CARD_EVENT_TYPE_ID } from '../../../constants/clickstreamEvents/renterDetailsModal';
import { emitClickOptionsFromOtherActionsMenuEvent } from '../../../constants/clickstreamEvents/otherActionsMenu';
import { LeadManagementContext } from '../../../contexts/LeadManagementContextProvider';
import useLeadManagementTableQuery, { useLeadLookupByConversationId } from '../../../hooks/useLeadManagementTableQuery';
import RenterDetailsHeader from '../../RenterDetails/RenterDetailsHeader';
import RenterDetailsProgressBar from '../../RenterDetails/RenterDetailsProgressBar';
import RenterDetailsScreeningCriteria from '../../RenterDetails/RenterDetailsScreeningCriteria';
import RenterDetailsLatestContact from '../../RenterDetails/RenterDetailsLatestContact';
import RenterDetailsNotepad from '../../RenterDetails/RenterDetailsNotepad';
import NextStepTextButton from '../../LeadManagementTable/components/NextStepTextButton';
import NextStepHelperText from '../../LeadManagementTable/components/NextStepHelperText';
import { leadManagementRenterDetailsSectionToScrollToStore } from '../../../lib/leadManagementStores';
import RenterDetailsTour from '../../RenterDetails/RenterDetailsTour';
import { useAuth } from '../../../AuthProvider';
import useModal from '../../../hooks/useModal';
import { ModalType } from '../../../types/ModalType';
import useLeadManagementSecondaryActions from '../../../hooks/useLeadManagementSecondaryActions';
import RenterDetailsAlerts from '../../RenterDetails/RenterDetailsAlerts';
import formatListingAddress from '../../../utils/formatListingAddress';
import { isEligibleForTourUpsell } from '../../../utils/tourUpsellUtils';

import '@zg-rentals/touring/src/styles/tourCalendar.scss';

export default function RenterDetailsModal(props: DrawerProps) {
  const { updateQueryParams } = useQueryParamsNext();
  const {
    clientTimeZone,
    conversationId: conversationIdFromUrl,
    conversationIdToFilterBy,
    folder,
    listingAlias,
    listingAliases,
    minCriteriaMatches,
    readRepliedStatus,
    renterProgressStatus,
  } = useContext(LeadManagementContext);

  // Pass the multi-listing filter so this query shares its cache key (and lead list) with the
  // table — otherwise prev/next would page through an unfiltered all-listings list under v2.
  const { leads, isLoading, data } = useLeadManagementTableQuery({
    clientTimeZone,
    conversationId: conversationIdToFilterBy,
    listingAlias,
    listingAliases,
    folder,
    minCriteriaMatches,
    readRepliedStatus,
    renterProgressStatus,
  });

  // Listing identity comes from the open lead, not page-level context (the context alias is the
  // page filter — comma-joined under multi-listing selection).
  const currentLeadIndex = leads.findIndex((lead) => lead.conversationId === conversationIdFromUrl);
  // Content falls back to a conversation-scoped lookup when the open lead isn't in the filtered
  // list (e.g. its property was deselected while the drawer is open) — prev/next navigation stays
  // list-based and its buttons already disable in that state.
  const lookupLead = useLeadLookupByConversationId(conversationIdFromUrl, clientTimeZone, {
    enabled: !isLoading && !!data && currentLeadIndex === -1 && !!conversationIdFromUrl,
  });
  const currentLead = (currentLeadIndex !== -1 ? leads[currentLeadIndex] : null) ?? lookupLead;
  const currentLeadListingAlias = currentLead?.listingDetails?.listingAlias ?? '';
  const currentLeadListingAddress = formatListingAddress(currentLead?.listingDetails?.address);

  const { hasUpsellCookieSetForListing } = useTourUpsellCookie(currentLeadListingAlias);

  const leadManagementRenterDetailsSectionToScrollTo = leadManagementRenterDetailsSectionToScrollToStore.useValue();

  const { data: touringTreatmentData } = useZCalendarWebApiHandler_getTouringTreatmentForZuidAndListing(
    {
      listingAlias: currentLeadListingAlias,
    },
    {
      enabled: !hasUpsellCookieSetForListing && !!currentLeadListingAlias,
    },
  );

  const [resetAllInlineFeedbackChildState, setResetAllInlineFeedbackChildState] = useState(false);
  const [shouldSaveNoteText, setShouldSaveNoteText] = useState(false);
  // Mirrors the scroll-to-section union. `renterDetailsHeader` scrolls to the top rather than
  // highlighting a section, so it never matches the `sectionToHighlight === …` checks below.
  const [sectionToHighlight, setSectionToHighlight] = useState<
    'latestContact' | 'notepad' | 'renterDetailsHeader' | 'renterProfile' | ''
  >('');
  const [isBodyRendered, setIsBodyRendered] = useState(false);
  const notepadSectionRef = useRef<HTMLDivElement>(null);
  const latestContactSectionRef = useRef<HTMLElement>(null);
  const renterDetailsHeaderRef = useRef<HTMLDivElement>(null);
  const screeningCriteriaSectionRef = useRef<HTMLParagraphElement>(null);
  const { user } = useAuth();
  const { setActiveModal } = useModal();
  const scrollToSections = {
    notepad: notepadSectionRef,
    latestContact: latestContactSectionRef,
    renterProfile: screeningCriteriaSectionRef,
  };

  const scrollToTop = {
    renterDetailsHeader: renterDetailsHeaderRef,
  };

  const isLoadingLeadsData = isLoading || !data;

  // The drawer renders its body inside a portal (FloatingPortal) that mounts a render-cycle after
  // the modal, so the section refs aren't attached on the modal's first commit. Flip this once the
  // body node mounts so the scroll effect re-runs with the refs in place — without it a cold open
  // (e.g. "Add note") scrolls before the target exists and, since the section variable doesn't
  // change again, never retries.
  const handleBodyRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      setIsBodyRendered(true);
    }
  }, []);

  useEffect(() => {
    if (isLoadingLeadsData || !leadManagementRenterDetailsSectionToScrollTo) return;

    const ref = scrollToSections[leadManagementRenterDetailsSectionToScrollTo as keyof typeof scrollToSections];
    const headerRef = scrollToTop[leadManagementRenterDetailsSectionToScrollTo as keyof typeof scrollToTop];

    if (ref) {
      scrollToSection(ref);
    }
    if (headerRef) {
      scrollToTopOfModal(headerRef);
    }
  }, [leadManagementRenterDetailsSectionToScrollTo, isLoadingLeadsData, isBodyRendered]);

  const handleApproveClick = useCallback(() => {
    emitClickOptionsFromOtherActionsMenuEvent({
      user,
      listing: { listingAlias: currentLeadListingAlias },
      triggerSourceName: 'approve',
      triggerObjectName: 'lead_profile_card',
      lead: { leadId: currentLead?.renterInfo?.leadId },
    });
    setActiveModal(ModalType.ApproveLeadModal);
  }, [user, currentLeadListingAlias, currentLead?.renterInfo?.leadId, setActiveModal]);

  const secondaryActions = useLeadManagementSecondaryActions(currentLead?.nextStepSecondaryActions, {
    onApprove: handleApproveClick,
  });

  if (isLoadingLeadsData) {
    return null;
  }

  const hasZrmToursEnabled = currentLead?.touring.hasZrmToursEnabled ?? false;
  const shouldShowRenterDetailsTour = hasZrmToursEnabled && !!currentLeadListingAlias && !!conversationIdFromUrl;
  const shouldShowTourUpsellBanner =
    isEligibleForTourUpsell(touringTreatmentData) && !currentLead?.applicationInfo?.isApplicationCompleted;
  const leadsSize = leads.length;

  const scrollToSection = (ref: React.RefObject<HTMLElement>) => {
    if (!ref.current) return null;

    ref.current.scrollIntoView({
      behavior: 'smooth',
      block: leadManagementRenterDetailsSectionToScrollTo === 'latestContact' ? 'center' : 'start',
    });

    setSectionToHighlight(leadManagementRenterDetailsSectionToScrollTo);
    leadManagementRenterDetailsSectionToScrollToStore.set('');

    setTimeout(() => {
      setSectionToHighlight('');
    }, 3000);
  };

  const scrollToTopOfModal = (headerRef: React.RefObject<HTMLElement>) => {
    if (!headerRef.current) return null;
    headerRef.current.scrollIntoView();
    leadManagementRenterDetailsSectionToScrollToStore.set('');
  };

  const handleResetAllInlineFeedbackChildStateComplete = () => {
    setResetAllInlineFeedbackChildState(false);
  };

  const handleNoteSavingComplete = () => {
    setShouldSaveNoteText(false);
  };

  const handleResetNotesInlineFeedbackAndSaveNoteText = () => {
    setResetAllInlineFeedbackChildState(true);
    setShouldSaveNoteText(true);
  };

  const handleClose = () => {
    handleResetNotesInlineFeedbackAndSaveNoteText();
    updateQueryParams({
      queryParamValues: {
        conversationId: null,
      },
      shouldReplaceHistory: true,
    });
  };

  const handleNavigateToNextLead = () => {
    if (currentLeadIndex < leadsSize - 1) {
      handleResetNotesInlineFeedbackAndSaveNoteText();
      updateQueryParams({
        queryParamValues: {
          conversationId: leads[currentLeadIndex + 1].conversationId,
        },
        shouldReplaceHistory: true,
      });
      leadManagementRenterDetailsSectionToScrollToStore.set('renterDetailsHeader');
    }
  };

  const handleNavigateToPreviousLead = () => {
    if (currentLeadIndex > 0) {
      handleResetNotesInlineFeedbackAndSaveNoteText();
      updateQueryParams({
        queryParamValues: {
          conversationId: leads[currentLeadIndex - 1].conversationId,
        },
        shouldReplaceHistory: true,
      });
      leadManagementRenterDetailsSectionToScrollToStore.set('renterDetailsHeader');
    }
  };

  const handleUpsellBannerClick = () => {
    emitLeadMgmtTourUpsellBannerClickEvent(user, { listingAlias: currentLeadListingAlias });
  };

  const header = (
    <Box
      data-eventid={OPEN_LEAD_PROFILE_CARD_EVENT_TYPE_ID}
      data-eventdescription="Open lead profile card"
      css={{
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        paddingInlineStart: 'looser',
      }}
    >
      <ButtonGroup
        aria-label="view previous or next renter details"
        css={{ position: 'absolute', left: 'layout.looser', top: '50%', transform: 'translate(-60%, -50%)' }}
      >
        <IconButton
          shape="circle"
          emphasis="bare"
          icon={<IconChevronUpFilled />}
          title="Previous"
          size="sm"
          onClick={handleNavigateToPreviousLead}
          disabled={currentLeadIndex <= 0}
        />
        <IconButton
          shape="circle"
          emphasis="bare"
          icon={<IconChevronDownFilled />}
          title="Next"
          size="sm"
          onClick={handleNavigateToNextLead}
          disabled={currentLeadIndex === -1 || currentLeadIndex === leadsSize - 1}
        />
      </ButtonGroup>
      <Heading level={2} textStyle="heading-xs">
        Renter details
      </Heading>
    </Box>
  );

  const body = (
    <Box
      ref={handleBodyRef}
      css={{
        paddingBottom: 'layout.loosest',
        md: {
          paddingInline: 'loose',
        },
      }}
    >
      <RenterDetailsHeader lead={currentLead} conversationId={conversationIdFromUrl} ref={renterDetailsHeaderRef} />
      <RenterDetailsProgressBar lead={currentLead} />

      <RenterDetailsAlerts alerts={currentLead?.alerts} />

      {shouldShowTourUpsellBanner && (
        <Box css={{ marginTop: 'loose' }}>
          <TourSchedulingUpsell
            data-eventid={LEAD_MNGMT_TOUR_UPSELL_BANNER_CLICK_EVENT_TYPE_ID}
            listingAlias={currentLeadListingAlias}
            modalHeaderText={currentLeadListingAddress}
            onClick={handleUpsellBannerClick}
          />
        </Box>
      )}

      {shouldShowRenterDetailsTour && <RenterDetailsTour leadId={currentLead?.renterInfo?.leadId} />}

      <RenterDetailsScreeningCriteria
        lead={currentLead}
        conversationId={conversationIdFromUrl}
        isBackgroundHighlighted={sectionToHighlight === 'renterProfile'}
        ref={screeningCriteriaSectionRef}
      />
      <RenterDetailsLatestContact
        lead={currentLead}
        conversationId={conversationIdFromUrl}
        isBackgroundHighlighted={sectionToHighlight === 'latestContact'}
        ref={latestContactSectionRef}
      />
      <RenterDetailsNotepad
        clientTimeZone={clientTimeZone}
        conversationId={conversationIdFromUrl}
        leadId={currentLead?.renterInfo?.leadId}
        listingAlias={currentLeadListingAlias}
        shouldRefetchStatusLabels={false}
        resetAllInlineFeedbackChildState={resetAllInlineFeedbackChildState}
        onResetAllInlineFeedbackChildState={handleResetAllInlineFeedbackChildStateComplete}
        shouldSaveNoteText={shouldSaveNoteText}
        onHandleNoteSavingComplete={handleNoteSavingComplete}
        isBackgroundHighlighted={sectionToHighlight === 'notepad'}
        ref={notepadSectionRef}
      />
    </Box>
  );

  const footer = () => (
    <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tight', width: '100%', textAlign: 'right' }}>
      <ButtonGroup aria-label="Renter details actions" css={{ justifyContent: 'flex-end', width: '100%' }}>
        {secondaryActions.map(({ action, handler }) => (
          <Button key={action.link.linkId} emphasis="outlined" tone="brand" onClick={handler}>
            {action.link.text}
          </Button>
        ))}
        <NextStepTextButton conversationId={conversationIdFromUrl} shouldDisplayAsButtonStyle={true} />
      </ButtonGroup>
      <NextStepHelperText conversationId={conversationIdFromUrl} textStyle="body-xs" />
    </Box>
  );

  return <Drawer {...props} renderHeader={header} renderBody={body} renderFooter={footer()} onClose={handleClose} />;
}
